import React from 'react';
import i18n from '../../../../i18n';
import { SideBar } from '../../sidebar';
import NoData from '../../../common/NoData';
import { TableSkeleton } from '../../../common/skelton/TableSkelton';
import FilterSection from '../../../common/Filter';
import { tableStyles } from '../../../../styles/components/js/tableStyle';
import DataTable from 'react-data-table-component';
import { getLoggedUser } from '../../../../state/user';
import { ClientAmountCard } from '../../../common/ClientAmountCard';
import HeaderBanner from '../../../common/HeaderBanner';
import { getTransactionRequestSummary, getTransactions } from './services/transactionService';
import { getBidders } from '../CashFlowDashboard/services/cashFlowService';
import Select from 'react-select';
import { selectStyles } from '../../../../styles/components/js/selectStyle';
import { formatDate } from '../../../../utils/common';
import { StatusNewBadge } from '../../../common/StatusBadge';
import { NEW_TRANSACTION_STATUS, TRANSACTION_TYPES } from '../../../../constants/status';
import { WithdrawalActionModal } from './components/WithdrawalActionModal';
import { AlertTriangle, Download, ExternalLink, FileSpreadsheet, Printer } from 'lucide-react';
import { imageUrl } from '../../../../constants/global';
import NewDropdown from '../../../common/NewDropDown';
import ColumnVisibilityDropdown, {
  applyColumnOrder,
} from '../../../common/ColumnVisibilityDropdown';
import { exportTransactionsReport } from '../Transaction/service/transactionService';

const user = getLoggedUser();

const roleAllowedStatuses = {
  A: ['pending', 'admin_approved', 'completed', 'admin_rejected', 'finance_rejected'],
  F: ['pending', 'admin_approved', 'completed', 'finance_rejected'],
  CE: ['pending', 'admin_approved', 'completed'],
  GM: ['pending', 'admin_approved', 'completed'],
};
const allStatuses = [
  {
    type: 'pending',
    title: i18n.t('awaiting_admin_approval'),
    amount: 0,
    auctionCount: 0,
    countLabel: i18n.t('transactions'),
  },
  {
    type: 'admin_approved',
    title: i18n.t('admin_approved'),
    amount: 0,
    auctionCount: 0,
    countLabel: i18n.t('transactions'),
  },
  {
    type: 'completed',
    title: i18n.t('finance_approved'),
    amount: 0,
    auctionCount: 0,
    countLabel: i18n.t('transactions'),
  },
  {
    type: 'admin_rejected',
    title: i18n.t('admin_rejected'),
    amount: 0,
    auctionCount: 0,
    countLabel: i18n.t('transactions'),
  },
  {
    type: 'finance_rejected',
    title: i18n.t('finance_rejected'),
    amount: 0,
    auctionCount: 0,
    countLabel: i18n.t('transactions'),
  },
];
const allowedStatuses = user ? roleAllowedStatuses[user.role] || [] : [];
const filteredMeta = allStatuses.filter((card) => allowedStatuses.includes(card.type));

const getResubmissionInfo = (row) => {
  const history = row?.status_history || [];

  if (history[0]?.action !== 'RESUBMITTED_TO_FINANCE') return null;

  // The resubmission entry's own `reason` is just a generic system note
  // ("Admin updated the withdrawal details and resubmitted it."). The actual
  // reason Finance gave lives on the FINANCE_REJECTED entry that precedes it.
  const rejectionEntry = history.find((h) => h.action === 'FINANCE_REJECTED');

  return {
    previousReason: rejectionEntry?.reason || '--',
    resubmittedOn: history[0]?.created_at,
  };
};

export class WithdrawalRequest extends React.Component {
  constructor() {
    super();
    this.state = {
      search: {
        startDate: null,
        endDate: null,
        bidder: '',
      },
      status: filteredMeta[0]?.type || 'pending', // default to first allowed
      activeTab: filteredMeta[0]?.type || 'pending',
      cardMeta: filteredMeta,
      bidders: [],
      transactions: [],
      isLoading: false,
      limit: 15,
      page: 1,
      total: 0,
      showAction: false,
      selectedRow: null,
      confirmAction: null,
      hiddenColumns: [],
      columnOrder: [],
    };

    this.handleSearchChange = this.handleSearchChange.bind(this);
  }
  componentDidMount() {
    window.scrollTo(0, 0);
    // Parse URL query params
    const params = new URLSearchParams(window.location.search);
    const search = {
      startDate: params.get('startDate') || null,
      endDate: params.get('endDate') || null,
      bidder: params.get('bidder') || '',
    };
    const defaultStatus =
      user?.role === 'A' ? 'pending' : user?.role === 'F' ? 'admin_approved' : 'pending';

    const status = params.get('status') || defaultStatus;
    const page = parseInt(params.get('page')) || 1;
    const limit = parseInt(params.get('limit')) || 15;

    this.setState({ search, status, page, limit, activeTab: status }, () => {
      this.fetchSummary();
      this.fetchTransactions();
      this.fetchBidderList();
    });
  }

  // Update URL without refreshing page
  updateUrlQuery(q) {
    const queryString = new URLSearchParams(
      Object.fromEntries(Object.entries(q).filter(([_, v]) => v !== '' && v !== null))
    ).toString();
    window.history.replaceState(null, '', `${window.location.pathname}?${queryString}`);
  }

  async fetchTransactions() {
    this.setState({ isloading: true });

    const { limit, page, activeTab } = this.state;
    const { startDate, endDate, bidder } = this.state.search;

    const q = {
      page,
      limit,
    };

    if (startDate) q.startDate = startDate;
    if (endDate) q.endDate = endDate;
    if (bidder) q.bidder = bidder;

    const apiQuery = {
      ...q,
      method: 'offline',
      type: 'withdraw',
    };

    const statusMap = {
      A: {
        pending: 'W',
        admin_approved: 'WA',
        completed: 'R',
        admin_rejected: 'RA',
        finance_rejected: 'RF',
      },
      F: {
        pending: 'W',
        admin_approved: 'WA',
        completed: 'R',
        finance_rejected: 'RF',
      },
      CE: {
        pending: 'W',
        admin_approved: 'WA',
        completed: 'R',
      },
      GM: {
        pending: 'W',
        admin_approved: 'WA',
        completed: 'R',
      },
    };

    apiQuery.status = statusMap[user?.role]?.[activeTab];

    // URL should contain the tab name
    this.updateUrlQuery({
      ...q,
      status: activeTab,
    });

    try {
      const response = await getTransactions(apiQuery);

      if (response.success) {
        this.setState({
          transactions: response.data,
          total: response.meta.total,
        });
      }
    } catch (error) {
      console.error('Transaction fetch failed:', error);
    } finally {
      this.setState({ isloading: false });
    }
  }

  async fetchSummary() {
    this.setState({ isSummaryLoading: true });

    try {
      const { startDate, endDate, bidder } = this.state.search;

      const q = { type: 'withdrawal' };
      if (startDate) q.startDate = startDate;
      if (endDate) q.endDate = endDate;
      if (bidder) q.bidder = bidder;

      const response = await getTransactionRequestSummary(q);

      if (response.success) {
        const cardMeta = filteredMeta.map((card) => ({
          ...card,
          amount: response.data[card.type]?.amount ?? 0,
          auctionCount: response.data[card.type]?.count ?? 0,
        }));

        this.setState({
          summary: response.data,
          cardMeta,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.setState({ isSummaryLoading: false });
    }
  }

  async fetchBidderList() {
    this.setState({ bidderLoading: true });

    try {
      const { startDate, endDate } = this.state.search;

      const q = {};
      if (startDate) q.startDate = startDate;
      if (endDate) q.endDate = endDate;

      const response = await getBidders(q);

      if (response.success) {
        const bidders = [
          {
            value: null,
            label: i18n.t('all') || 'All',
          },
          ...response.data.map((item) => ({
            value: item.id,
            label: `${item.name} (${item.id})`,
          })),
        ];
        this.setState({
          bidders,
          bidderLoading: false,
        });
      }
    } catch (error) {
      console.error('Failed to fetch bidders', error);
    } finally {
      this.setState({ bidderLoading: false });
    }
  }

  //  Bidder onChange — store as integer
  handleBidderChange = (selectedOption) => {
    this.setState((prevState) => ({
      search: {
        ...prevState.search,
        bidder: selectedOption ? parseInt(selectedOption.value) : null,
      },
    }));
  };

  handleSearchChange = (e) => {
    const { name, value } = e.target;

    this.setState((prevState) => {
      const updatedSearch = { ...prevState.search, [name]: value };
      return { search: updatedSearch };
    });
  };

  handleSearch = () => {
    this.fetchSummary();
    this.fetchTransactions();
  };

  handleClear = () => {
    const defaultSearch = {
      startDate: null,
      endDate: null,
      bidder: null,
    };

    this.setState(
      {
        search: defaultSearch,
        page: 1,
      },
      () => {
        this.fetchSummary();
        this.fetchTransactions();
      }
    );
  };

  onChangePage = (page) => {
    this.setState({ page: page }, () => {
      this.fetchTransactions();
    });
  };

  onChangeRowsPerPage = (limit) => {
    this.setState({ limit: limit, page: 1 }, () => {
      this.fetchTransactions();
    });
  };

  handleCardSelect = (cardType) => {
    this.setState(
      {
        activeTab: cardType,
        status: cardType,
        page: 1,
      },
      () => {
        this.fetchSummary();
        this.fetchTransactions();
      }
    );
  };

  handleActions = (row) => {
    this.setState({
      showAction: true,
      selectedRow: row,
    });
  };

  getColumns = () => {
    const isAdmin = user?.role === 'A';
    const isFinance = user?.role === 'F';
    const { hiddenColumns, columnOrder } = this.state;

    const columns = [
      {
        id: 'sl_no',
        name: i18n.t('sl_no'),
        width: '100px',
        sortable: true,
        wrap: true,
        cell: (row, i) => i + 1,
      },
      {
        id: 'action',
        name: i18n.t('action'),
        minWidth: '90px',
        center: true,
        cell: (row) => {
          const canShow =
            (isAdmin && ['W', 'WA', 'RF'].includes(row.status)) ||
            (isFinance && row.status === 'WA');

          return canShow ? (
            <div className="action-buttons">
              <button
                className="button1 button1-primary button1-action"
                onClick={() => this.handleActions(row)}
                disabled={Number(row.debit) <= 0}
              >
                {i18n.t('action')}
              </button>
            </div>
          ) : (
            <span className="attachment-empty">No Action</span>
          );
        },
      },
      {
        id: 'id',
        name: i18n.t('id'),
        width: '90px',
        wrap: true,
        cell: (row) => (
          <div className="table-row-info">
            <span className="highlight-table-value">{row.id}</span>
            <a
              href={`/enrolled_users`}
              target="_blank"
              rel="noreferrer"
              className="table-row-info__wallet"
            >
              {i18n.t('enroll')}
              <ExternalLink size={13} />
            </a>
          </div>
        ),
      },
    ];

    if (isAdmin) {
      columns.push({
        id: 'bidder_id',
        name: i18n.t('bidder_id'),
        width: '100px',
        cell: (row) => (
          <div className="table-row-info">
            <span className="highlight-table-value">{row.user}</span>

            <a
              href={`/mzadcom-transactions-summary?bidder=${row.user}`}
              target="_blank"
              rel="noreferrer"
              className="table-row-info__wallet"
            >
              {i18n.t('wallet')}
              <ExternalLink size={13} />
            </a>
          </div>
        ),
      });
    }

    if (isFinance) {
      columns.push(
        {
          id: 'bidder_id',
          name: i18n.t('bidder_id'),
          width: '100px',
          cell: (row) => (
            <div className="table-row-info">
              <span className="highlight-table-value">{row.user}</span>

              <a
                href={`/mzadcom-transactions-summary?bidder=${row.user}`}
                target="_blank"
                rel="noreferrer"
                className="table-row-info__wallet"
              >
                {i18n.t('wallet')}
                <ExternalLink size={13} />
              </a>
            </div>
          ),
        },
        {
          // NOTE: 'name'/'username' get pushed again, unconditionally,
          // further down for every role — finance users currently see
          // both sets rendered (pre-existing behavior, not something I
          // changed). Using distinct ids here so the visibility toggle
          // doesn't accidentally control both at once; worth deciding
          // whether one of the two pairs should just be removed.
          id: 'finance_name',
          name: i18n.t('name'),
          width: '120px',
          cell: (row) => <span className="highlight-table-value">{row?.user_relation?.name}</span>,
          wrap: true,
        },
        {
          id: 'finance_username',
          name: i18n.t('username'),
          width: '120px',
          cell: (row) => (
            <span className="highlight-table-value">{row?.user_relation?.username}</span>
          ),
          wrap: true,
        }
      );
    }

    if (['admin_approved', 'completed'].includes(this.state.activeTab)) {
      columns.push({
        id: 'project_name',
        name: i18n.t('project_name'),
        width: '140px',
        cell: (row) => <span className="highlight-table-value-secondary">{row.remarks}</span>,
      });
    }

    columns.push(
      {
        id: 'bank',
        name: i18n.t('bank'),
        width: '110px',
        cell: (row) => <span>{row.bank}</span>,
      },
      {
        id: 'beneficiary',
        name: i18n.t('beneficiary'),
        width: '110px',
        cell: (row) => <span>{row.beneficiary ? row.beneficiary : '--'}</span>,
      },
      {
        id: 'ac_no',
        name: i18n.t('ac_no'),
        width: '110px',
        cell: (row) => <span>{row.account_number}</span>,
      },
      {
        id: 'withdraw',
        name: i18n.t('withdraw'),
        width: '100px',
        right: true,
        cell: (row) => <span className="highlight-table-value-secondary">{row.debit}</span>,
      },
      {
        id: 'balance',
        name: i18n.t('balance'),
        width: '100px',
        right: true,
        cell: (row) => <span className="highlight-table-value-secondary">{row.balance}</span>,
      },
      {
        id: 'requested_date',
        name: i18n.t('requested_date'),
        width: '170px',
        cell: (row) => (
          <span className="highlight-table-value-secondary">{formatDate(row.created_at)}</span>
        ),
      },
      {
        id: 'status',
        width: '170px',
        name: i18n.t('status'),
        cell: (row) => <StatusNewBadge status={NEW_TRANSACTION_STATUS[row.status]} />,
        wrap: true,
      }
    );
    if (['admin_approved'].includes(this.state.activeTab)) {
      columns.push({
        id: 'resubmission_notice',
        name: i18n.t('resubmission_notice'),
        minWidth: '280px',
        wrap: true,
        cell: (row) => {
          const info = getResubmissionInfo(row);

          if (!info) return <span className="attachment-empty">--</span>;

          return (
            <div className="resubmission-warning">
              <div className="resubmission-warning__header">
                <AlertTriangle
                  size={14}
                  className="resubmission-warning__icon"
                  aria-hidden="true"
                />
                <span className="resubmission-warning__title">
                  {i18n.t('previously_rejected_by_finance_msg')}
                </span>
              </div>
              <div className="resubmission-warning__body">
                <div className="resubmission-warning__row">
                  <span className="resubmission-warning__label">{i18n.t('previous_reason')}</span>
                  <span className="resubmission-warning__value">{info.previousReason}</span>
                </div>
                <div className="resubmission-warning__row">
                  <span className="resubmission-warning__label">{i18n.t('admin_resubmitted')}</span>
                  <span className="resubmission-warning__value">
                    {formatDate(info.resubmittedOn)}
                  </span>
                </div>
              </div>
            </div>
          );
        },
      });
    }
    if (isFinance || (isAdmin && !['pending', 'rejected'].includes(this.state.activeTab))) {
      columns.push({
        id: 'admin_approved',
        name: i18n.t('admin_aprroved'),
        width: '150px',
        cell: (row) =>
          row.admin_approved_on ? (
            <span className="highlight-table-value-secondary">
              {formatDate(row.admin_approved_on)}
            </span>
          ) : (
            <span className="attachment-empty">No Data</span>
          ),
      });
    }
    if (['completed'].includes(this.state.activeTab)) {
      columns.push({
        id: 'finance_approved',
        name: i18n.t('finance_aprroved'),
        width: '150px',
        cell: (row) =>
          row.fm_approved_on ? (
            <span className="highlight-table-value-secondary">
              {formatDate(row.fm_approved_on)}
            </span>
          ) : (
            <span className="attachment-empty">No Data</span>
          ),
      });
    }
    if (!['pending', 'admin_approved', 'completed'].includes(this.state.activeTab)) {
      columns.push({
        id: 'reject_reason',
        name: i18n.t('reject_reason'),
        width: '150px',
        cell: (row) => <span className="highlight-table-value-secondary">{row.reject_reason}</span>,
      });
    }

    columns.push(
      {
        id: 'receipt',
        name: i18n.t('receipt'),

        width: '130px',
        cell: (row) =>
          row.file_receipt ? (
            <a
              className="button-table"
              href={`${imageUrl}uploads/wallet/receipt/${row.file_receipt}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FileSpreadsheet size={18} /> {i18n.t('receipt')}
            </a>
          ) : (
            <span className="attachment-empty">No Receipt</span>
          ),
        wrap: true,
      },
      {
        id: 'attachment',
        name: i18n.t('attachment'),
        width: '150px',
        cell: (row) => {
          const paymentReceipts = row.payment_receipts || [];
          const attachmentReceipts = row.attachment_receipts || [];

          return (
            <div className="attachment-list">
              {/* Main Receipt */}
              {row.file_fm && (
                <a
                  href={`${imageUrl}uploads/wallet/receipt/${row.file_fm}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="attachment-link receipt"
                >
                  {i18n.t('document')}
                </a>
              )}

              {/* Payment Receipts */}
              {paymentReceipts.map((item, index) => (
                <a
                  key={`payment-${index}`}
                  href={`${imageUrl}uploads/wallet/receipt/${item.receipt_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="attachment-link payment"
                >
                  {i18n.t('payment-receipt')} {index + 1}
                </a>
              ))}

              {/* Attachment Receipts */}
              {attachmentReceipts.map((item, index) => (
                <a
                  key={`attachment-${index}`}
                  href={`${imageUrl}uploads/wallet/receipt/${item.document}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="attachment-link document"
                >
                  {i18n.t('document')} {index + 1}
                </a>
              ))}

              {/* Empty State */}
              {!row.file_fm && paymentReceipts.length === 0 && attachmentReceipts.length === 0 && (
                <span className="attachment-empty">No Attachment</span>
              )}
            </div>
          );
        },
      }
    );
    columns.push(
      {
        id: 'name',
        name: i18n.t('name'),
        width: '120px',
        cell: (row) => <span className="highlight-table-value">{row?.user_relation?.name}</span>,
        wrap: true,
      },
      {
        id: 'username',
        name: i18n.t('username'),
        width: '120px',
        cell: (row) => (
          <span className="highlight-table-value">{row?.user_relation?.username}</span>
        ),
        wrap: true,
      },
      {
        id: 'mobile',
        name: i18n.t('mobile'),
        width: '100px',
        cell: (row) => <span>{row?.user_relation?.mobile}</span>,
        wrap: true,
      },
      {
        id: 'email',
        name: i18n.t('email'),
        width: '120px',
        cell: (row) => <span>{row?.user_relation?.email}</span>,
        wrap: true,
      },
      {
        id: 'transaction',
        name: i18n.t('transaction'),
        width: '140px',
        cell: (row) => <span>{row.transaction_id}</span>,
      },
      {
        id: 'ref',
        name: i18n.t('ref'),
        width: '180px',
        cell: (row) => <span>{row.reference}</span>,
      },
      {
        id: 'type',
        name: i18n.t('type'),
        width: '100px',
        cell: (row) => <span>{TRANSACTION_TYPES[row.type]?.text || row.type || '-'}</span>,
      }
    );

    if (this.state.activeTab === 'admin_rejected') {
      columns.push({
        id: 'wallet_refund',
        name: i18n.t('wallet_refund'),
        width: '160px',
        cell: (row) => {
          if (row.refunded_to_wallet === null || row.refunded_to_wallet === undefined) {
            return <span className="attachment-empty">--</span>;
          }
          return row.refunded_to_wallet ? (
            <div className="table-row-info">
              <span className="highlight-table-value" style={{ color: '#059669' }}>
                {i18n.t('refunded')} · {row.refunded_amount}
              </span>
              <span className="highlight-table-value-secondary">{formatDate(row.refunded_at)}</span>
            </div>
          ) : (
            <span className="highlight-table-value" style={{ color: '#b45309' }}>
              {i18n.t('not_refunded')}
            </span>
          );
        },
      });
    }

    const ordered = applyColumnOrder(columns, columnOrder);

    return ordered.map((col) => ({
      ...col,
      omit: col.id ? hiddenColumns.includes(col.id) : false,
    }));
  };

  getColumnMeta = () => {
    return this.getColumns()
      .filter((c) => c.id)
      .map((c) => ({
        id: c.id,
        label: typeof c.name === 'string' ? c.name : c.id,
        alwaysVisible: c.id === 'sl_no' || c.id === 'id',
      }));
  };

  handleDownload = async (fileType) => {
    const { activeTab } = this.state;
    const { startDate, endDate, bidder } = this.state.search;

    let exportApi = exportTransactionsReport;
    let fileName = 'Withdrawal_Report';

    const apiQuery = {
      method: 'offline',
      type: 'withdraw',
      fileType,
    };
    if (bidder) apiQuery.bidder = bidder;
    if (startDate) apiQuery.startDate = startDate;
    if (endDate) apiQuery.endDate = endDate;

    const statusMap = {
      A: {
        pending: 'W',
        admin_approved: 'WA',
        completed: 'R',
        admin_rejected: 'RA',
        finance_rejected: 'RF',
      },
      F: {
        pending: 'W',
        admin_approved: 'WA',
        completed: 'R',
        finance_rejected: 'RF',
      },
      CE: {
        pending: 'W',
        admin_approved: 'WA',
        completed: 'R',
      },
      GM: {
        pending: 'W',
        admin_approved: 'WA',
        completed: 'R',
      },
    };

    apiQuery.status = statusMap[user?.role]?.[activeTab];

    this.setState({ exportLoading: true });

    try {
      await exportApi({
        params: apiQuery,
        fileType,
        fileName,
      });
    } finally {
      this.setState({ exportLoading: false });
    }
  };

  render() {
    const {
      bidders,
      bidderLoading,
      transactions,
      isloading,
      showAction,
      selectedRow,
      exportLoading,
      activeTab, // NEW — needed for the ColumnVisibilityDropdown's tableKey
    } = this.state;

    const { startDate, endDate } = this.state.search;

    return (
      <React.Fragment>
        <HeaderBanner
          title={i18n.t('withdrawal_requests')}
          subtitle={i18n.t('track_withdrawal_requests')}
        />
        <section className="dashboard-section">
          <div className="container">
            <div className="row">
              <div className="col-xl-3 col-lg-12 col-md-12">
                {!this.state.hideSidebar && <SideBar />}
              </div>
              <div
                className={
                  this.state.hideSidebar
                    ? 'col-xl-12 col-lg-12 col-md-12'
                    : 'col-xl-9 col-lg-12 col-md-12'
                }
              >
                <div className="card">
                  <div className="card-header">
                    <div className="expand-dashboard">
                      <span
                        style={{ fontSize: '1rem', marginRight: '0.5rem', cursor: 'pointer' }}
                        onClick={() => {
                          this.setState({
                            hideSidebar: !this.state.hideSidebar,
                          });
                        }}
                      >
                        {this.state.hideSidebar && <i className="icon-size-actual"></i>}
                        {!this.state.hideSidebar && <i className="icon-size-fullscreen"></i>}
                      </span>
                    </div>
                    <div className="header-action">
                      <ColumnVisibilityDropdown
                        tableKey={`withdrawal_requests_${user?.role}_${activeTab}`}
                        columns={this.getColumnMeta()}
                        onChange={(hidden, order) =>
                          this.setState({ hiddenColumns: hidden, columnOrder: order })
                        }
                      />
                      <NewDropdown
                        label={i18n.t('export')}
                        loading={exportLoading}
                        disabled={transactions.length === 0}
                        icon={Download}
                        items={[
                          {
                            label: i18n.t('excel'),
                            icon: FileSpreadsheet,
                            onClick: () => this.handleDownload('excel'),
                          },
                          {
                            label: i18n.t('pdf'),
                            icon: Printer,
                            onClick: () => this.handleDownload('pdf'),
                          },
                        ]}
                      />
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="client-cards-container-new">
                      {this.state.cardMeta.map((config) => (
                        <ClientAmountCard
                          key={config.type}
                          {...config}
                          selected={this.state.activeTab === config.type}
                          onSelect={this.handleCardSelect}
                        />
                      ))}
                    </div>
                    <FilterSection
                      loading={this.state.isLoading}
                      onSearch={this.handleSearch}
                      onClear={this.handleClear}
                    >
                      {/* Start Date */}
                      <div className="select-wrapper floating-label-wrapper">
                        <input
                          id="startDate"
                          type="date"
                          name="startDate"
                          value={startDate || ''}
                          onChange={this.handleSearchChange}
                          className="filter-base floating-input"
                          placeholder=" " // Important: single space for floating effect
                        />
                        <label htmlFor="startDate" className="floating-label">
                          {i18n.t('start_date')}
                        </label>
                      </div>

                      {/* End Date */}
                      <div className="select-wrapper floating-label-wrapper">
                        <input
                          id="endDate"
                          type="date"
                          name="endDate"
                          value={endDate || ''}
                          onChange={this.handleSearchChange}
                          className="filter-base floating-input"
                          placeholder={i18n.t('end_date')}
                        />
                        <label htmlFor="endDate" className="floating-label">
                          {i18n.t('end_date')}
                        </label>
                      </div>

                      {/* Bidder Dropdown */}

                      <div className="react-select-wrapper">
                        <Select
                          options={bidders}
                          value={bidders.find((c) => c.value === this.state.search.bidder) || null}
                          onChange={this.handleBidderChange}
                          name="bidder"
                          styles={selectStyles}
                          menuPlacement="auto"
                          menuPortalTarget={document.body}
                          placeholder={bidderLoading ? `${i18n.t('loading')}...` : i18n.t('all')}
                          isLoading={bidderLoading}
                          isDisabled={bidderLoading}
                          isClearable
                        />
                        <label className="react-select-label">{i18n.t('bidder')}</label>
                      </div>
                    </FilterSection>

                    <div className="Data-Listing">
                      {isloading ? (
                        <TableSkeleton columns={10} rows={10} />
                      ) : transactions.length ? (
                        <DataTable
                          responsive={true}
                          columns={this.getColumns()}
                          data={transactions}
                          customStyles={tableStyles}
                          highlightOnHover
                          pagination
                          paginationServer
                          paginationTotalRows={this.state.total}
                          paginationPerPage={this.state.limit}
                          paginationDefaultPage={this.state.page}
                          paginationRowsPerPageOptions={[15, 25, 50, 100]}
                          paginationComponentOptions={{
                            rowsPerPageText: i18n.t('rows_per_page'),
                            rangeSeparatorText: i18n.t('out'),
                          }}
                          onChangePage={this.onChangePage}
                          onChangeRowsPerPage={this.onChangeRowsPerPage}
                          conditionalRowStyles={[
                            {
                              when: (row) => getResubmissionInfo(row) !== null,
                              style: {
                                backgroundColor: '#fff8d6',
                              },
                            },
                          ]}
                        />
                      ) : (
                        <NoData
                          message={i18n.t('no_transactions_available')}
                          actionText={i18n.t('refresh')}
                          onAction={this.fetchTransactions.bind(this)}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <WithdrawalActionModal
          show={showAction}
          selectedTransaction={selectedRow}
          role={user?.role}
          onClose={() =>
            this.setState({
              showAction: false,
              selectedRow: null,
            })
          }
          onComplete={() => {
            this.fetchSummary();
            this.fetchTransactions();
            this.setState({ showAction: false, selectedRow: null });
          }}
        />
      </React.Fragment>
    );
  }
}
